import React from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { useApolloClient } from '@apollo/client';

import Accordion, {
  AccordionContent,
  AccordionTitle
} from '@buyside/components/presentation/Accordion/accordionComponents';
import HighlightText from '@buyside/components/presentation/Twitter/HighlightText/highlightText';
import ParsedHtml from '@buyside/components/presentation/ParsedHtml/parsedHtml';
import PdfViewer from '@buyside/components/presentation/IdeaShowPage/Thesis/pdfViewer';
import Skeleton, { SKELETON_WIDTHS } from '@buyside/components/presentation/Skeleton/skeleton';

import { IdeaCoreQuery } from '@interfaces/graphql-typings';

const IdeaCore = require('buyside/graphql/queries/ideaShowPage/IdeaCore');

import styles from '@buyside/components/presentation/IdeaShowPage/Thesis/thesisStyles.scss';

const Thesis: React.FC<{ ideaId: string; isSelectable: boolean; loading: boolean }> = ({
  ideaId,
  isSelectable,
  loading
}) => {
  const client = useApolloClient();

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCore,
    variables: { ideaId }
  })?.idea;

  const {
    autoclosed,
    closedAt,
    canCurrentUserViewIdeaFilterDetails,
    dateToClose,
    pdfThesisUrl,
    legacyPositionDisclaimer,
    positionDisclaimerFund,
    positionDisclaimerIndividual,
    thesis
  } = cachedIdea || {};

  let disclaimerText = null;
  if (positionDisclaimerIndividual && positionDisclaimerFund) {
    disclaimerText =
      "The author of this idea and the author's fund had a position in this security at the time of posting and may trade in and out of this position without informing the SumZero community.";
  } else if (positionDisclaimerIndividual) {
    disclaimerText =
      'The author of this idea had a position in this security at the time of posting and may trade in and out of this position without informing the SumZero community.';
  } else if (positionDisclaimerFund) {
    disclaimerText =
      "The author's fund had a position in this security at the time of posting and may trade in and out of this position without informing the SumZero community.";
  } else if (legacyPositionDisclaimer) {
    disclaimerText =
      "The author and/or the author's fund had a position in this security at the time of posting and may trade in and out of this position without informing the SumZero community.";
  }

  return (
    <div id="idea_thesis" className={styles.ideaThesis}>
      <Accordion basic initialOpen={canCurrentUserViewIdeaFilterDetails}>
        <AccordionTitle
          collapseIcon={faMinus}
          dataTag="thesis"
          expandable={Boolean(canCurrentUserViewIdeaFilterDetails)}
          expandIcon={faPlus}
          isLocked={
            canCurrentUserViewIdeaFilterDetails !== undefined &&
            !canCurrentUserViewIdeaFilterDetails
          }
          loading={loading}
        >
          Thesis
        </AccordionTitle>
        <AccordionContent>
          {loading ? (
            <div>
              <Skeleton width={SKELETON_WIDTHS.FULL} />
              <Skeleton width={SKELETON_WIDTHS.FULL} />
              <Skeleton width={SKELETON_WIDTHS.FULL} />
              <Skeleton width={SKELETON_WIDTHS.FULL} />
              <Skeleton width={SKELETON_WIDTHS.TWO_THIRD} />
            </div>
          ) : (
            <div
              data-testid="content"
              className={classnames({ [styles.hideContent]: !canCurrentUserViewIdeaFilterDetails })}
            >
              {autoclosed && closedAt && (
                <div className={styles.autoclose}>
                  This idea automatically closed on{' '}
                  {moment(new Date(closedAt)).format('MMM D, YYYY')} due to{' '}
                  {dateToClose ? 'hitting its target price' : 'timeframe expiration'}. Details
                  above.
                </div>
              )}
              {!closedAt && disclaimerText && (
                <div className={styles.disclaimer}>
                  <strong>Disclaimer:</strong> {disclaimerText}
                </div>
              )}
              {pdfThesisUrl ? (
                <PdfViewer fileUrl={pdfThesisUrl} />
              ) : (
                <>
                  {thesis && (
                    <ParsedHtml html={thesis}>
                      {({ content, tinyMceStyles }): React.ReactNode =>
                        isSelectable ? (
                          <HighlightText>
                            <div className={tinyMceStyles}>{content}</div>
                          </HighlightText>
                        ) : (
                          <div className={tinyMceStyles}>{content}</div>
                        )
                      }
                    </ParsedHtml>
                  )}
                </>
              )}
            </div>
          )}
        </AccordionContent>
      </Accordion>
    </div>
  );
};

export default Thesis;
